// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { ArrowDown, Copy, Download, MoreHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  type ImportEntry,
  type ImportUserRef,
  type ImportVendorRef,
  useDownloadImportFile,
} from "@/api/hooks/use-imports";
import { DataTable } from "@/components/data-table";
import { ImportRetriggerIcon } from "@/components/icons";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { FilterSelect } from "@/components/shared/filter-select";
import { ImportSourceBadge } from "@/components/shared/import-source-badge";
import { ImportStatusBadge } from "@/components/shared/import-status-badge";
import { ImportTypeBadge } from "@/components/shared/import-type-badge";
import { RowId } from "@/components/shared/row-id";
import { StoreLink } from "@/components/shared/store-link";
import { TimezoneFooterNote } from "@/components/shared/timezone-footer-note";
import { UserLink } from "@/components/shared/user-link";
import { VendorLink } from "@/components/shared/vendor-link";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RetriggerImportDialog, type RetriggerImportTarget } from "@/features/imports/retrigger-import-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { formatDuration } from "@/lib/format";

export type ImportsTableScope = "store" | "chain" | "ingress";

export interface ImportsTableStore {
  storeId: number;
  storeName: string;
  storeDisplay: string;
}

export interface ImportsTableChain {
  chainName: string;
  chainDisplay: string;
}

export interface ImportsTableProps {
  scope: ImportsTableScope;
  storageKey: string;

  // Data
  imports: ImportEntry[];
  count: number;
  isLoading: boolean;
  isFetching?: boolean;
  refetch?: () => void;

  // Stores list (only used when scope !== "store"). Used both to resolve `storeId` → the route
  // param `storeName` for the Store column link, and to populate the Store filter dropdown.
  // Pass the full chain/scope's stores (not just per-page) so the filter isn't empty on load.
  stores?: ImportsTableStore[];

  // Chains list (only used when scope === "ingress") — populates the Chain filter dropdown.
  chains?: ImportsTableChain[];

  // User + vendor refs from the imports response — used to resolve `createdBy` (which can refer
  // to either a user or a vendor depending on `createdBySource`).
  users?: ImportUserRef[];
  vendors?: ImportVendorRef[];

  // Pagination + sorting
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;

  // Search + filters
  search: string;
  onSearchChange: (value: string) => void;
  chainFilter?: string;
  onChainFilterChange?: (value: string) => void;
  storeFilter?: string;
  onStoreFilterChange?: (value: string) => void;
  importTypeFilter: string;
  onImportTypeFilterChange: (value: string) => void;
  sourceFilter: string;
  onSourceFilterChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;

  // Whether the user has permission to mutate imports (retrigger, etc.) in the current scope.
  canEdit?: boolean;

  // Optional toolbar + export
  toolbar?: React.ReactNode;
  onExport?: () => Promise<void>;
}

export function ImportsTable({
  scope,
  storageKey,
  imports,
  count,
  isLoading,
  isFetching,
  refetch,
  stores,
  chains,
  users,
  vendors,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  search,
  onSearchChange,
  chainFilter,
  onChainFilterChange,
  storeFilter,
  onStoreFilterChange,
  importTypeFilter,
  onImportTypeFilterChange,
  sourceFilter,
  onSourceFilterChange,
  statusFilter,
  onStatusFilterChange,
  canEdit,
  toolbar,
}: ImportsTableProps) {
  const { t } = useTranslation("back-office");
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const [retriggerTarget, setRetriggerTarget] = useState<RetriggerImportTarget | null>(null);

  // Build a lookup so the Store column can resolve storeId → the route param `storeName`.
  const storeMap = useMemo(() => {
    const map = new Map<number, ImportsTableStore>();
    for (const s of stores ?? []) map.set(s.storeId, s);
    return map;
  }, [stores]);
  const userMap = useMemo(() => {
    const map = new Map<number, ImportUserRef>();
    for (const u of users ?? []) map.set(u.userId, u);
    return map;
  }, [users]);
  const vendorMap = useMemo(() => {
    const map = new Map<number, ImportVendorRef>();
    for (const v of vendors ?? []) map.set(v.vendorId, v);
    return map;
  }, [vendors]);

  const storeOptions = useMemo(
    () =>
      [...(stores ?? [])]
        .sort((a, b) => (a.storeDisplay || a.storeName).localeCompare(b.storeDisplay || b.storeName))
        .map((s) => ({ label: s.storeDisplay || s.storeName, value: s.storeName })),
    [stores],
  );
  const chainOptions = useMemo(
    () =>
      [...(chains ?? [])]
        .sort((a, b) => (a.chainDisplay || a.chainName).localeCompare(b.chainDisplay || b.chainName))
        .map((c) => ({ label: c.chainDisplay || c.chainName, value: c.chainName })),
    [chains],
  );
  const sourceOptions = useMemo(
    () => (context?.importSources ?? []).map((s) => ({ label: s, value: s })),
    [context?.importSources],
  );
  const typeOptions = useMemo(
    () => (context?.importTypes ?? []).map((tt) => ({ label: tt, value: tt })),
    [context?.importTypes],
  );
  const statusOptions = useMemo(
    () => (context?.importStatuses ?? []).map((s) => ({ label: s, value: s })),
    [context?.importStatuses],
  );

  const showStoreColumn = scope !== "store";

  const columns = useMemo<ColumnDef<ImportEntry, unknown>[]>(() => {
    const cols: ColumnDef<ImportEntry, unknown>[] = [
      {
        accessorKey: "importRequestId",
        header: () => (
          // Sorting is globally disabled on this table, but the API always returns rows in
          // ID desc. Surface that visually with a static down arrow so users know.
          <span className="inline-flex items-center gap-1">
            {t("imports.columns.id")}
            <ArrowDown className="h-3.5 w-3.5" />
          </span>
        ),
        size: 100,
        enableHiding: false,
        meta: { columnLabel: t("imports.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.importRequestId} />,
      },
      {
        accessorKey: "publicId",
        header: t("imports.columns.publicId"),
        meta: { columnLabel: t("imports.columns.publicId"), defaultHidden: true, className: "font-mono" },
        cell: ({ row }) => row.original.publicId ?? "—",
      },
      {
        accessorKey: "importType",
        header: t("imports.columns.type"),
        meta: { columnLabel: t("imports.columns.type") },
        cell: ({ row }) => <ImportTypeBadge type={row.original.importType} />,
      },
    ];

    if (showStoreColumn) {
      cols.push({
        accessorKey: "storeCode",
        header: t("imports.columns.store"),
        meta: { columnLabel: t("imports.columns.store") },
        cell: ({ row }) => {
          const storeId = row.original.storeId;
          const storeCode = row.original.storeCode;
          if (storeId != null) {
            const store = storeMap.get(storeId);
            if (store) {
              return <StoreLink storeName={store.storeName} legibleName={store.storeDisplay || storeCode} />;
            }
          }
          return storeCode || "—";
        },
      });
    }

    cols.push(
      {
        accessorKey: "source",
        header: t("imports.columns.source"),
        meta: { columnLabel: t("imports.columns.source") },
        cell: ({ row }) => <ImportSourceBadge source={row.original.source ?? "api"} />,
      },
      {
        accessorKey: "processingTime",
        header: t("imports.columns.processingTime"),
        meta: { columnLabel: t("imports.columns.processingTime") },
        cell: ({ row }) => formatDuration(row.original.processingTime),
      },
      {
        accessorKey: "validationTime",
        header: t("imports.columns.validationTime"),
        meta: { columnLabel: t("imports.columns.validationTime"), defaultHidden: true },
        cell: ({ row }) => formatDuration(row.original.validationTime),
      },
      {
        accessorKey: "queueingTime",
        header: t("imports.columns.queueingTime"),
        meta: { columnLabel: t("imports.columns.queueingTime"), defaultHidden: true },
        cell: ({ row }) => formatDuration(row.original.queueingTime),
      },
      {
        accessorKey: "itemsFound",
        header: t("imports.columns.itemsFound"),
        meta: { columnLabel: t("imports.columns.itemsFound") },
        cell: ({ row }) => row.original.itemsFound ?? "—",
      },
      {
        accessorKey: "productsFound",
        header: t("imports.columns.productsFound"),
        meta: { columnLabel: t("imports.columns.productsFound"), defaultHidden: true },
        cell: ({ row }) => row.original.productsFound ?? "—",
      },
      {
        accessorKey: "productsCreated",
        header: t("imports.columns.productsCreated"),
        meta: { columnLabel: t("imports.columns.productsCreated"), defaultHidden: true },
        cell: ({ row }) => row.original.productsCreated ?? "—",
      },
      {
        accessorKey: "productsUpdated",
        header: t("imports.columns.productsUpdated"),
        meta: { columnLabel: t("imports.columns.productsUpdated"), defaultHidden: true },
        cell: ({ row }) => row.original.productsUpdated ?? "—",
      },
      {
        accessorKey: "productsZeroed",
        header: t("imports.columns.productsZeroed"),
        meta: { columnLabel: t("imports.columns.productsZeroed"), defaultHidden: true },
        cell: ({ row }) => row.original.productsZeroed ?? "—",
      },
      {
        accessorKey: "createdBy",
        header: t("imports.columns.createdBy"),
        meta: { columnLabel: t("imports.columns.createdBy") },
        cell: ({ row }) => {
          if (row.original.source === "cronjob") {
            const raw = row.original.cronjobName;
            if (!raw) return "—";
            const match = raw.match(/^(.+?)\s*\((.+)\)\s*$/);
            if (!match) return raw;
            return (
              <div className="leading-tight">
                <div>{match[1]}</div>
                <div className="text-[10px] text-muted-foreground font-mono">{match[2]}</div>
              </div>
            );
          }
          const createdBy = row.original.createdBy;
          if (createdBy == null) return "—";
          if (row.original.createdBySource === "vendor") {
            const vendor = vendorMap.get(createdBy);
            return <VendorLink vendorName={vendor?.name} legibleName={vendor?.name ?? `#${createdBy}`} />;
          }
          if (row.original.createdBySource === "user") {
            const user = userMap.get(createdBy);
            if (!user) return `#${createdBy}`;
            const display = user.fullName || user.email;
            return <UserLink publicId={user.publicId} legibleName={display} />;
          }
          return createdBy;
        },
      },
      {
        accessorKey: "createdAt",
        header: t("imports.columns.timestamp"),
        meta: { columnLabel: t("imports.columns.timestamp") },
        cell: ({ row }) => <DateTimeValue value={row.original.createdAt} />,
      },
      {
        accessorKey: "status",
        header: t("imports.columns.status"),
        enableHiding: false,
        meta: { columnLabel: t("imports.columns.status") },
        cell: ({ row }) => <ImportStatusBadge status={row.original.status} />,
      },
      {
        id: "actions",
        size: 50,
        enableHiding: false,
        enableSorting: false,
        meta: { forceWidth: true, sticky: "right" },
        cell: ({ row }) => <ImportRowActions entry={row.original} canEdit={canEdit} onRetrigger={setRetriggerTarget} />,
      },
    );

    // Sorting is intentionally disabled across the imports list until perf work lands on
    // the import_requests table; flip enableSorting back on per-column to restore it.
    return cols.map((c) => ({ ...c, enableSorting: false }));
  }, [t, showStoreColumn, storeMap, userMap, vendorMap, canEdit]);

  return (
    <>
      <DataTable
        storageKey={storageKey}
        columns={columns}
        data={imports}
        totalCount={count}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        sorting={sorting}
        onSortingChange={onSortingChange}
        onRefresh={refetch}
        isRefreshing={isFetching}
        // Export disabled while we sort out perf issues on the import_requests table.
        // onExport={onExport}
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={t("imports.searchPlaceholder")}
        searchDebounceMs={300}
        filters={
          <>
            {onChainFilterChange && (
              <FilterSelect
                label={t("chains.title")}
                value={chainFilter ?? ""}
                onChange={onChainFilterChange}
                options={chainOptions}
                searchPlaceholder={t("stores.operations.searchOption")}
                emptyText={t("stores.operations.noOption")}
              />
            )}
            {showStoreColumn && onStoreFilterChange && (
              <FilterSelect
                label={t("imports.columns.store")}
                value={storeFilter ?? ""}
                onChange={onStoreFilterChange}
                options={storeOptions}
                searchPlaceholder={t("stores.operations.searchOption")}
                emptyText={t("stores.operations.noOption")}
              />
            )}
            <FilterSelect
              label={t("imports.columns.type")}
              value={importTypeFilter}
              onChange={onImportTypeFilterChange}
              options={typeOptions}
              searchPlaceholder={t("stores.operations.searchOption")}
              emptyText={t("stores.operations.noOption")}
              loading={contextLoading}
            />
            <FilterSelect
              label={t("imports.columns.source")}
              value={sourceFilter}
              onChange={onSourceFilterChange}
              options={sourceOptions}
              searchPlaceholder={t("stores.operations.searchOption")}
              emptyText={t("stores.operations.noOption")}
            />
            <FilterSelect
              label={t("imports.columns.status")}
              value={statusFilter}
              onChange={onStatusFilterChange}
              options={statusOptions}
              searchPlaceholder={t("stores.operations.searchOption")}
              emptyText={t("stores.operations.noOption")}
              loading={contextLoading}
            />
          </>
        }
        footerNote={<TimezoneFooterNote />}
        toolbar={toolbar}
      />
      <RetriggerImportDialog target={retriggerTarget} onClose={() => setRetriggerTarget(null)} />
    </>
  );
}

// Each import carries up to four files: `json`/`csv` resolve to a download link via
// the ingress download endpoint; `json_gs`/`csv_gs` are the raw GCS paths exposed for
// copy-to-clipboard. CSV variants are optional.
type ImportFileType = "json" | "json_gs" | "csv" | "csv_gs";

/** Finds the first file on an import entry whose type matches (case-insensitive). */
function findFile(entry: ImportEntry, type: ImportFileType) {
  return entry.files?.find((f) => f.type?.toLowerCase() === type);
}

/** Triggers a browser download of `url` (an object/blob URL) as `filename`. */
function triggerAnchorDownload(url: string, filename: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function ImportRowActions({
  entry,
  canEdit,
  onRetrigger,
}: {
  entry: ImportEntry;
  canEdit?: boolean;
  onRetrigger: (target: RetriggerImportTarget) => void;
}) {
  const { t } = useTranslation("back-office");
  const download = useDownloadImportFile();

  const jsonFile = findFile(entry, "json");
  const csvFile = findFile(entry, "csv");
  const jsonGsFile = findFile(entry, "json_gs");
  const csvGsFile = findFile(entry, "csv_gs");

  async function handleDownload(fileUrl: string, filename: string) {
    try {
      // Query the API for the file's public download URL.
      const redirectUrl = await download.mutateAsync(fileUrl);
      try {
        // Fetch the file as a blob and download that via an object URL — the
        // browser only honours the `download` attribute (forcing a save instead of
        // opening inline) for same-origin/blob URLs, not the cross-origin signed URL.
        const fileRes = await fetch(redirectUrl);
        if (!fileRes.ok) throw new Error(`Download failed (${fileRes.status})`);
        const objectUrl = URL.createObjectURL(await fileRes.blob());
        triggerAnchorDownload(objectUrl, filename);
        URL.revokeObjectURL(objectUrl);
      } catch {
        // CORS or network failure on the signed URL — fall back to opening it in a
        // new tab so the file is still reachable (may render inline).
        window.open(redirectUrl, "_blank", "noopener");
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : "";
      toast.error(t("imports.actions.downloadError"), detail ? { description: detail } : undefined);
    }
  }

  function handleCopy(fileUrl: string) {
    navigator.clipboard.writeText(fileUrl);
    toast(
      <span>
        {t("common.copiedToClipboard")} <b className="break-all">{fileUrl}</b>
      </span>,
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        <DropdownMenuItem
          disabled={!canEdit}
          onSelect={() =>
            onRetrigger({
              importRequestId: entry.importRequestId,
              importType: entry.importType,
              storeCode: entry.storeCode,
            })
          }
        >
          <ImportRetriggerIcon className="h-4 w-4" />
          {t("imports.actions.retrigger")}
        </DropdownMenuItem>
        {(jsonFile || csvFile || jsonGsFile || csvGsFile) && <DropdownMenuSeparator />}
        {jsonFile && (
          <DropdownMenuItem onSelect={() => handleDownload(jsonFile.url, `import-${entry.importRequestId}.json`)}>
            <Download className="h-4 w-4" />
            {t("imports.actions.downloadJson")}
          </DropdownMenuItem>
        )}
        {csvFile && (
          <DropdownMenuItem onSelect={() => handleDownload(csvFile.url, `import-${entry.importRequestId}.csv`)}>
            <Download className="h-4 w-4" />
            {t("imports.actions.downloadCsv")}
          </DropdownMenuItem>
        )}
        {jsonGsFile && (
          <DropdownMenuItem onSelect={() => handleCopy(jsonGsFile.url)}>
            <Copy className="h-4 w-4" />
            {t("imports.actions.copyRawJson")}
          </DropdownMenuItem>
        )}
        {csvGsFile && (
          <DropdownMenuItem onSelect={() => handleCopy(csvGsFile.url)}>
            <Copy className="h-4 w-4" />
            {t("imports.actions.copyRawCsv")}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
